import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { api } from '../services/api';
import { ClientConfig } from '../types';
import { findDefinitionReferences, stripDefinitionReferences, DefinitionReference } from '../utils/configReferences';
import { useConfigStore } from '../stores/useConfigStore';
import { PrimaryButton } from '../components/common/PrimaryButton';
import { SecondaryButton } from '../components/common/SecondaryButton';
import { EntityDefinitionsEditor } from '../components/settings/EntityDefinitionsEditor';
import { FeatureDefinitions } from '../components/settings/FeatureDefinitions';
import { StageDefinitions } from '../components/settings/StageDefinitions';
import { HealthScoreConfig } from '../components/settings/HealthScoreConfig';
import { EntityFeatureMapping } from '../components/settings/EntityFeatureMapping';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

/**
 * Settings Page
 * Configure entities, features, stages, health scoring, and integrations
 */
const SETTINGS_TABS = ['entities', 'features', 'stages', 'health-scoring', 'mappings'];

export const SettingsPage: React.FC = () => {
  const { tab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const activeTab = useMemo(() => {
    const idx = SETTINGS_TABS.indexOf(tab || '');
    return idx >= 0 ? idx : 0;
  }, [tab]);
  const [config, setConfig] = useState<ClientConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Load configuration
  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getClientConfig();
      setConfig(data);
    } catch (err: any) {
      console.error('Error loading config:', err);
      setError(err.response?.data?.message || 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!config) return;

    try {
      setSaving(true);
      setError(null);

      // Extract only the configurable fields
      const updates = {
        entityDefinitions: config.entityDefinitions,
        featureDefinitions: config.featureDefinitions,
        stageConfig: config.stageConfig,
        healthScoreConfig: config.healthScoreConfig,
        entityFeatureMapping: config.entityFeatureMapping,
        alertConfig: config.alertConfig,
      };

      await api.updateClientConfig(updates);

      setSuccessMessage('Configuration saved successfully');
      setHasChanges(false);

      // Force refresh the Zustand config store cache
      useConfigStore.getState().loadConfig(true);
    } catch (err: any) {
      console.error('Error saving config:', err);
      setError(err.response?.data?.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    navigate(`/settings/${SETTINGS_TABS[newValue]}`, { replace: true });
  };

  const handleConfigChange = (updates: Partial<ClientConfig>) => {
    if (!config) return;
    setConfig({ ...config, ...updates });
    setHasChanges(true);
  };

  /**
   * Deleting an entity or feature definition that stage rules, health-score
   * components or mappings still reference would silently break them
   * (unreachable stage, zero-point component). Ask first, then cascade.
   */
  const [pendingDelete, setPendingDelete] = useState<{
    kind: 'entity' | 'feature';
    keys: string[];
    refs: DefinitionReference[];
    updates: Partial<ClientConfig>;
  } | null>(null);

  const handleDefinitionsChange = (kind: 'entity' | 'feature', next: Record<string, any>) => {
    if (!config) return;
    const field = kind === 'entity' ? 'entityDefinitions' : 'featureDefinitions';
    const previous = (config[field] || {}) as Record<string, any>;
    const removed = Object.keys(previous).filter((k) => !(k in next));
    const refs = removed.flatMap((k) => findDefinitionReferences(config, kind, k));
    if (removed.length > 0 && refs.length > 0) {
      setPendingDelete({ kind, keys: removed, refs, updates: { [field]: next } });
      return;
    }
    handleConfigChange({ [field]: next });
  };

  const confirmPendingDelete = () => {
    if (!config || !pendingDelete) return;
    let stripped: Pick<ClientConfig, 'stageConfig' | 'healthScoreConfig' | 'entityFeatureMapping'> = config;
    for (const key of pendingDelete.keys) {
      stripped = stripDefinitionReferences(stripped, pendingDelete.kind, key);
    }
    handleConfigChange({ ...pendingDelete.updates, ...stripped });
    setPendingDelete(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !config) {
    return (
      <Box>
        <Alert severity="error">{error}</Alert>
        <SecondaryButton onClick={loadConfig} sx={{ mt: 2 }}>
          Retry
        </SecondaryButton>
      </Box>
    );
  }

  if (!config) {
    return <Alert severity="error">No configuration found</Alert>;
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={600}>
            Settings
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Configure your LogoPulse analytics platform
          </Typography>
        </Box>

        <PrimaryButton
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={!hasChanges || saving}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </PrimaryButton>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3, pt: 2, pb: 1 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              '& .MuiTab-root': {
                color: '#666',
                fontWeight: 500,
                textTransform: 'none',
                fontSize: '0.95rem',
                minHeight: 48,
                borderRadius: '8px',
                transition: 'all 0.2s',
                mb: 1,
                '&:hover': {
                  bgcolor: '#f5f5f5',
                },
              },
              '& .Mui-selected': {
                bgcolor: '#DCF995',
                color: '#1B0826 !important',
                fontWeight: 600,
              },
              '& .MuiTabs-indicator': {
                display: 'none',
              },
            }}
          >
            <Tab label="Entities" />
            <Tab label="Features" />
            <Tab label="Lifecycle Stages" />
            <Tab label="Health Scoring" />
            <Tab label="Mappings" />
          </Tabs>
        </Box>

        <Box sx={{ p: 3 }}>
          {/* Entity Definitions */}
          <TabPanel value={activeTab} index={0}>
            <EntityDefinitionsEditor
              entityDefinitions={config.entityDefinitions || {}}
              onChange={(entityDefinitions) => handleDefinitionsChange('entity', entityDefinitions)}
            />
          </TabPanel>

          {/* Feature Definitions */}
          <TabPanel value={activeTab} index={1}>
            <FeatureDefinitions
              featureDefinitions={config.featureDefinitions || {}}
              onChange={(featureDefinitions) => handleDefinitionsChange('feature', featureDefinitions)}
            />
          </TabPanel>

          {/* Stage Definitions */}
          <TabPanel value={activeTab} index={2}>
            <StageDefinitions
              stageConfig={config.stageConfig as any}
              onChange={(stageConfig) => handleConfigChange({ stageConfig: stageConfig as any })}
              entityDefinitions={config.entityDefinitions || {}}
              featureDefinitions={config.featureDefinitions || {}}
            />
          </TabPanel>

          {/* Health Score Configuration */}
          <TabPanel value={activeTab} index={3}>
            <HealthScoreConfig
              healthScoreConfig={config.healthScoreConfig}
              onChange={(healthScoreConfig) => handleConfigChange({ healthScoreConfig })}
              entityDefinitions={config.entityDefinitions || {}}
              featureDefinitions={config.featureDefinitions || {}}
            />
          </TabPanel>

          {/* Entity-Feature Mapping */}
          <TabPanel value={activeTab} index={4}>
            <EntityFeatureMapping
              entityFeatureMapping={config.entityFeatureMapping || {}}
              entityDefinitions={config.entityDefinitions || {}}
              featureDefinitions={config.featureDefinitions || {}}
              onChange={(entityFeatureMapping) => handleConfigChange({ entityFeatureMapping })}
            />
          </TabPanel>
        </Box>
      </Paper>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
      />

      {/* Cascade-delete confirmation */}
      <Dialog open={!!pendingDelete} onClose={() => setPendingDelete(null)}>
        <DialogTitle>Still referenced elsewhere</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {pendingDelete && (
              <>
                <Typography variant="body2" gutterBottom>
                  {pendingDelete.kind === 'entity' ? 'Entity' : 'Feature'}{' '}
                  <strong>{pendingDelete.keys.join(', ')}</strong> is used by:
                </Typography>
                <ul style={{ marginTop: 4 }}>
                  {pendingDelete.refs.map((r, i) => (
                    <li key={i}><Typography variant="body2">{r.label}</Typography></li>
                  ))}
                </ul>
                <Typography variant="body2">
                  Deleting it will remove those conditions, components and mappings as well. Remember to Save afterwards.
                </Typography>
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={() => setPendingDelete(null)}>Cancel</SecondaryButton>
          <PrimaryButton onClick={confirmPendingDelete}>Delete and clean up</PrimaryButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
